/**
 * Tenant service shell — buyer flow + org-admin route subtree
 * mounted on tenant subdomains (``demo.forgmatic.com``,
 * ``*.forgmatic.com``, custom CNAMEs). Default in dev / e2e (the
 * Playwright suite drives buyer URLs against localhost).
 *
 * Excluded by design: SaaS-marketing pages
 * (for-providers / for-business / pricing-plans / providers) and
 * sign-up + onboarding flows that only make sense on the marketing
 * domain. Sign-in + password-reset stay so a returning customer can
 * re-auth from the tenant URL without bouncing to the platform.
 */

import {
  Navigate,
  useParams,
  useSearchParams,
  type RouteObject,
} from "react-router-dom";

import { RequirePageVisible } from "@/components/admin/RequirePageVisible";
import { RequirePageEnabled } from "@/components/edit/RequirePageEnabled";
import { withRouteErrorBoundary } from "@/components/error/withRouteErrorBoundary";
import { LegacyCheckoutRedirect } from "@/components/LegacyCheckoutRedirect";
import { LEGACY_CHECKOUT_SEGMENT } from "@/lib/storefrontPaths";

import {
  Account,
  AccountAddresses,
  DeveloperApps,
  DemoShopEnd,
  AccountEmailChangeConfirm,
  AuthVerifyEmail,
  AdminApiTokens,
  AdminAuditLog,
  AdminBilling,
  AdminBusinessInfo,
  AdminCompanyProfile,
  AdminContactInbox,
  AdminContacts,
  AdminMail,
  AdminCapacityPricing,
  AdminPlatformCompanyProfile,
  AdminPlatformQuoteSettings,
  AdminTenantQuoteSettings,
  AdminTenantMaterialProperties,
  AdminCarbon,
  AdminCustomerDetail,
  AdminDataExport,
  AdminDataResidency,
  AdminLegalDocuments,
  AdminDisputes,
  AdminSalesChannels,
  AdminFeatureSettings,
  AdminMenuLayout,
  AdminExtensions,
  AdminLayout,
  AdminOrderRules,
  AdminGroups,
  AdminAppHost,
  AdminNewsletter,
  AdminTemplates,
  AdminBoards,
  AdminOrderAssembly,
  AdminOrderDetail,
  AdminOrders,
  AdminMyOrganizations,
  AdminOverview,
  AdminPartnerDetail,
  AdminPartnerQueue,
  AdminPartners,
  AdminPipelineEditor,
  AdminVatSettings,
  AdminBaseCurrency,
  AdminTenantOperations,
  AdminProviderCustomerDetail,
  AdminProviderRanking,
  AdminInvoices,
  AdminInvoiceNew,
  AdminInvoiceDetail,
  AdminNumberSeries,
  AdminQuoteDetail,
  AdminQuoteNew,
  AdminQuotes,
  AdminSamlSetup,
  AdminOidcSetup,
  AdminSandbox,
  AdminItems,
  AdminOperationRates,
  AdminCostingScheme,
  AdminPricingLab,
  AdminPricingInputs,
  AdminItemDetail,
  AdminMaterialVariantDetail,
  AdminPickList,
  AdminStockMovements,
  AdminStorageLocations,
  AdminFinishedStock,
  AdminShopfloor,
  AdminMap,
  ShopfloorLayout,
  ShopfloorPlaces,
  ShopfloorOverview,
  ShopfloorFulfillment,
  ShopfloorPlaceTasks,
  ShopfloorMyTasks,
  ShopfloorStation,
  ShopfloorPair,
  ShopfloorContainerDisassembly,
  AdminSuppliers,
  AdminSupplierDetail,
  AdminPurchaseOrders,
  AdminPurchaseOrderDetail,
  AdminMaterialBatches,
  AdminTrace,
  AdminMachines,
  AdminShipping,
  AdminMarkets,
  AdminClaims,
  AdminStripeConnect,
  AdminApprovals,
  AdminWebhooks,
  Approvals,
  BuyerAddressBook,
  BuyerCapacityPurchase,
  BuyerDashboard,
  BuyerInvoices,
  BuyerReports,
  Cart,
  Checkout,
  Contact,
  CustomerOrgOnboarding,
  HowItWorks,
  Landing,
  Lazy,
  LegalPage,
  Materials,
  MyProjects,
  MyProjectDetail,
  MyProjectItemConfigure,
  NotFound,
  OrderHistory,
  OrderThanks,
  PasswordResetConfirm,
  SsoLinkConfirm,
  PasswordResetRequest,
  TwoFactorBootstrap,
  Pricing,
  OnboardingStart,
  ProviderOnboarding,
  PublicOrg,
  PublicQuote,
  QualityReport,
  QuoteBatch,
  RefundAccount,
  QuoteDetail,
  ServiceLanding,
  SignIn,
  Subprocessors,
  Track,
} from "./lazyPages";
import { AdminFallbackRoute } from "./AdminFallbackRoute";
import { LegacySignInRedirect } from "./LegacySignInRedirect";

/**
 * KAN-385 — „Aplikace" a „Integrace" splynuly do jedné plochy.
 *
 * Obě staré adresy vedou na ni. Uložená záložka na ``/admin/apps`` je
 * po sloučení nejběžnější vstup, jaký tenhle admin má; nechat ji spadnout
 * na 404 by znamenalo, že se sloučení projeví jako výpadek.
 */
function LegacyExtensionsRedirect(): JSX.Element {
  const { lang = "cs" } = useParams<{ lang?: string }>();
  return <Navigate to={`/${lang}/admin/extensions`} replace />;
}

function LegacyPlanRedirect(): JSX.Element {
  const { lang = "cs" } = useParams<{ lang?: string }>();
  return <Navigate to={`/${lang}/admin/billing#plan`} replace />;
}

function LegacyTenantOnboardingRedirect({
  target,
}: {
  target: "onboarding" | "onboarding/start";
}) {
  const { lang = "cs" } = useParams<{ lang?: string }>();
  return <Navigate to={`/${lang}/admin/${target}`} replace />;
}

/** KAN-1750 — krátký odkaz ``/{lang}/tp/{kód}`` vede na párování tabletu. */
function ShortPairingLink() {
  const { lang = "cs", code = "" } = useParams<{ lang?: string; code?: string }>();
  return <Navigate to={`/${lang}/shopfloor/pair/${encodeURIComponent(code)}`} replace />;
}

/**
 * KAN-716 — ``/sluzby/:slug`` byl jediný český slug v jinak anglické
 * sadě (``/how-it-works``, ``/materials``, ``/pricing``, ``/contact``,
 * ``/track``), takže zákazník pod prefixem ``/cs`` viděl uprostřed
 * navigace přepnutý jazyk. Sjednoceno na ``/services/:slug``.
 *
 * Stará adresa tu zůstává jako redirect, ne jako 404: proces pages jsou
 * deep-linkovatelné (ProcessPicker, ServicesDropdown, „nahradit soubor")
 * a tenant je mohl rozeslat zákazníkům. ``slug`` se přenáší beze změny —
 * mění se jen segment cesty, ne identita modulu.
 */
function LegacyServiceSlugRedirect(): JSX.Element {
  const { lang = "cs", slug = "" } = useParams<{
    lang?: string;
    slug?: string;
  }>();
  return <Navigate to={`/${lang}/services/${slug}`} replace />;
}

/**
 * KAN-28 (M1) — staré adresy obou map vedou na střechu, ne na 404.
 *
 * Query se PŘENÁŠÍ: `?node=` je deep link do editoru grafu a bookmark
 * na konkrétní uzel musí přežít přestěhování. `lens` se dosazuje, jen
 * když si ho odkaz sám nenese.
 */
function LegacyMapRedirect({ lens }: { lens: "process" | "hall" }) {
  const { lang = "cs" } = useParams<{ lang?: string }>();
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  if (!next.has("lens")) next.set("lens", lens);
  return <Navigate to={`/${lang}/admin/map?${next.toString()}`} replace />;
}

/**
 * KAN-26 — staré adresy obou boardů vedou na Tabuli, ne na 404.
 *
 * Query se PŘENÁŠÍ (vzor Mapy): ``groupBy`` se dosazuje, jen když si
 * ho odkaz sám nenese.
 */
function LegacyBoardsRedirect({ groupBy }: { groupBy: "order" | "operation" }) {
  const { lang = "cs" } = useParams<{ lang?: string }>();
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  if (!next.has("groupBy")) next.set("groupBy", groupBy);
  return <Navigate to={`/${lang}/admin/boards?${next.toString()}`} replace />;
}

/**
 * Staré adresy vestavěné Kvality (``/admin/quality``, ``/admin/qa``,
 * ``/admin/ncr``, detail vad zakázky) → host aplikace ``quality``
 * (KAN-1364). Odkazy z e-mailů a záložky musí žít. Sekci přenést nejde:
 * host předává aplikaci jen ``page_id``, ne query, takže jde jen cesta.
 */
function LegacyQualityRedirect() {
  const { lang = "cs" } = useParams<{ lang?: string }>();
  return <Navigate to={`/${lang}/admin/apps/quality`} replace />;
}

export const tenantChildren: RouteObject[] = [
  { index: true, element: <Landing /> },
  { path: "quote/:quoteId", element: <QuoteDetail /> },
  { path: "quote-batch/:publicId", element: <Lazy><QuoteBatch /></Lazy> },
  { path: "quotes/by-id/:publicId", element: <Lazy><PublicQuote /></Lazy> },
  // KAN-1675 — zákazník vyplní účet pro vrácení peněz převodem.
  { path: "refund-account/:token", element: <Lazy><RefundAccount /></Lazy> },
  { path: "cart", element: <Cart /> },
  { path: "checkout", element: <Checkout /> },
  // KAN-35 — „Zaplatit" na ukázkovém obchodě platformy vede sem, ne na objednávku.
  { path: "demo-shop", element: <Lazy><DemoShopEnd /></Lazy> },
  // KAN-922 — the breadcrumb linked here for months; old links must not 404.
  { path: LEGACY_CHECKOUT_SEGMENT, element: <LegacyCheckoutRedirect /> },
  { path: "order/:orderNumber", element: <OrderThanks /> },
  { path: "track", element: <RequirePageEnabled pageKey="track"><Lazy><Track /></Lazy></RequirePageEnabled> },
  { path: "contact", element: <RequirePageEnabled pageKey="contact"><Lazy><Contact /></Lazy></RequirePageEnabled> },
  { path: "materials", element: <RequirePageEnabled pageKey="materials"><Lazy><Materials /></Lazy></RequirePageEnabled> },
  { path: "pricing", element: <RequirePageEnabled pageKey="pricing"><Lazy><Pricing /></Lazy></RequirePageEnabled> },
  { path: "account", element: <Lazy><Account /></Lazy> },
  { path: "account/addresses", element: <Lazy><AccountAddresses /></Lazy> },
  // Vydavatelská plocha appek. Pod ``account``, NE pod ``admin``:
  // vlastnictví řeší BE přes ``publisher_user_id``, kdežto admin shell
  // vyžaduje tenantského admina — externí vývojář by se na svou
  // vlastní plochu nedostal.
  { path: "account/developer-apps", element: <Lazy><DeveloperApps /></Lazy> },
  // Landing for the verify link mailed by /account/email-change/request.
  // Unauthenticated — opened from the NEW inbox, often another device.
  { path: "account/email-change/:token", element: <Lazy><AccountEmailChangeConfirm /></Lazy> },
  // KAN-335 — landing for the address-verification link mailed by
  // POST /auth/sign-up. Same both-shells reasoning as the line above.
  { path: "auth/verify-email/:token", element: <Lazy><AuthVerifyEmail /></Lazy> },
  { path: "account/orders", element: <Lazy><OrderHistory /></Lazy> },
  { path: "account/projects", element: <Lazy><MyProjects /></Lazy> },
  { path: "account/projects/:publicId", element: <Lazy><MyProjectDetail /></Lazy> },
  { path: "account/projects/:publicId/items/:itemSlug", element: <Lazy><MyProjectItemConfigure /></Lazy> },
  { path: "sign-in", element: <Lazy><SignIn /></Lazy> },
  { path: "login", element: <LegacySignInRedirect /> },
  { path: "2fa-setup", element: <Lazy><TwoFactorBootstrap /></Lazy> },
  { path: "password-reset", element: <Lazy><PasswordResetRequest /></Lazy> },
  { path: "password-reset/:token", element: <Lazy><PasswordResetConfirm /></Lazy> },
  // KAN-169 — where the account-link e-mail lands. Mounted on BOTH
  // shells: the mail points at the tenant's own host when there is a
  // tenant apex, and falls back to the platform host when there is not.
  { path: "sso/link/:token", element: <Lazy><SsoLinkConfirm /></Lazy> },
  // Právní stránky obchodu. Slug odpovídá ``legal_document_kinds.public_slug``
  // ve scope ``tenant``, takže ``/terms`` je tady obchodní podmínky
  // PRODÁVAJÍCÍHO, ne platformy. ``complaints`` a ``withdrawal`` jsou
  // samostatné dokumenty, protože reklamační řád a poučení o odstoupení
  // musí být dohledatelné samy o sobě, ne schované v odstavci podmínek.
  { path: "privacy", element: <Lazy><LegalPage pageKey="privacy" /></Lazy> },
  { path: "terms", element: <Lazy><LegalPage pageKey="terms" /></Lazy> },
  { path: "complaints", element: <Lazy><LegalPage pageKey="complaints" /></Lazy> },
  { path: "withdrawal", element: <Lazy><LegalPage pageKey="withdrawal" /></Lazy> },
  { path: "gdpr", element: <Lazy><LegalPage pageKey="gdpr" /></Lazy> },
  { path: "legal/subprocessors", element: <Lazy><Subprocessors /></Lazy> },
  // KAN-1243 — cesta, kterou uvádí text DPA (čl. 7.2, Příloha 3). Stejná
  // úvaha jako na platformním shellu: přesměrovat, ne 404.
  { path: "subprocessors", element: <Navigate to="../legal/subprocessors" relative="path" replace /> },
  { path: "how-it-works", element: <RequirePageEnabled pageKey="how_it_works"><Lazy><HowItWorks /></Lazy></RequirePageEnabled> },
  // KAN-2109 — zákaznická fronta nemá adminovou bránu tarifu: ta mluvila
  // o plánu obchodu a „návratu do adminu". Bez ``b2b.approval`` vrací API
  // prázdnou frontu a menu odkaz neukáže.
  { path: "approvals", element: <Lazy><Approvals /></Lazy> },
  { path: "buyer/:customerOrgId", element: <Lazy><BuyerDashboard /></Lazy> },
  { path: "buyer/:customerOrgId/addresses", element: <Lazy><BuyerAddressBook /></Lazy> },
  { path: "buyer/:customerOrgId/reports", element: <Lazy><BuyerReports /></Lazy> },
  { path: "buyer/:customerOrgId/invoices", element: <Lazy><BuyerInvoices /></Lazy> },
  { path: "buyer/:providerSlug/capacity", element: <Lazy><BuyerCapacityPurchase /></Lazy> },
  { path: "o/:slug", element: <Lazy><PublicOrg /></Lazy> },
  { path: "quality-report/:orderNumber", element: <Lazy><QualityReport /></Lazy> },
  // Never gated: the ``services`` switch hides only the header dropdown
  // (KAN-1563) — upload routing and the process cards lead here.
  { path: "services/:slug", element: <Lazy><ServiceLanding /></Lazy> },
  // Předchůdce dnešní ``services/:slug`` — viz LegacyServiceSlugRedirect.
  { path: "sluzby/:slug", element: <LegacyServiceSlugRedirect /> },
  // Onboarding wizards live here (not on platform shell) so the
  // admin status queries inside the wizard resolve via the tenant
  // subdomain context instead of the platform shell's "default"
  // tenant fallback. OrgsVerify + SignUpTeamFinalize cross-host
  // the user here once the BE assigns a slug.
  // R4b — mini-interview + vyber sablony (spec_tenant_onboarding §4.6).
  {
    path: "onboarding/start",
    element: <LegacyTenantOnboardingRedirect target="onboarding/start" />,
  },
  {
    path: "onboarding/provider",
    element: <LegacyTenantOnboardingRedirect target="onboarding" />,
  },
  {
    path: "onboarding/provider/:step",
    element: <LegacyTenantOnboardingRedirect target="onboarding" />,
  },
  { path: "onboarding/customer-org", element: <Lazy><CustomerOrgOnboarding /></Lazy> },
  // Dílna (spec_shopfloor_module D1b) — terminál pracoviště MIMO admin
  // zónu: smí sem i role production (operátoři), která do /admin
  // nemůže. Vlastní chrome (ShellFrame v App.tsx vynechává storefront
  // header/footer stejně jako pro /admin).
  {
    path: "shopfloor",
    element: <Lazy><ShopfloorLayout /></Lazy>,
    children: withRouteErrorBoundary([
      { index: true, element: <Lazy><ShopfloorPlaces /></Lazy> },
      // KAN-246 — přehled mistra; MUSÍ být nad "places/:placeRef",
      // ať se "overview" nechytne jako id místa.
      { path: "overview", element: <Lazy><ShopfloorOverview /></Lazy> },
      // KAN-2023 — expedice; nad "places/:placeRef" ze stejného důvodu.
      { path: "fulfillment", element: <Lazy><ShopfloorFulfillment /></Lazy> },
      { path: "places/:placeRef", element: <Lazy><ShopfloorPlaceTasks /></Lazy> },
      { path: "my", element: <Lazy><ShopfloorMyTasks /></Lazy> },
      // KAN-1981 — místo tabletu, zafixované (bez „Změnit").
      { path: "station", element: <Lazy><ShopfloorStation /></Lazy> },
      // KAN-1750 — párování tabletu (QR kód / krátký kód + heslo).
      { path: "pair", element: <Lazy><ShopfloorPair /></Lazy> },
      { path: "pair/:code", element: <Lazy><ShopfloorPair /></Lazy> },
      // KAN-305 — rozebrání kontejneru: bez slugu je to hledání
      // (scan/zápis), se slugem rozhodování per dítě.
      { path: "disassembly", element: <Lazy><ShopfloorContainerDisassembly /></Lazy> },
      {
        path: "disassembly/:itemSlug",
        element: <Lazy><ShopfloorContainerDisassembly /></Lazy>,
      },
    ]),
  },
  // KAN-1750 — krátký odkaz na párování tabletu; přesměruje do dílny.
  { path: "tp/:code", element: <ShortPairingLink /> },
  {
    path: "admin",
    element: <Lazy><AdminLayout /></Lazy>,
    children: withRouteErrorBoundary([
      { index: true, element: <Lazy><AdminOverview /></Lazy> },
      { path: "onboarding", element: <Lazy><ProviderOnboarding /></Lazy> },
      { path: "onboarding/start", element: <Lazy><OnboardingStart /></Lazy> },
      // Self-service account for the logged-in admin — reuses the
      // customer ``Account`` page verbatim, rendered inside the admin
      // shell. Linked from ``AdminUserMenu`` (no nav-group entry).
      { path: "account", element: <Lazy><Account /></Lazy> },
      // KAN-1357 — the user's own tenants, across tenants. Same
      // arrangement as ``account`` above: an account-level page rendered
      // inside the admin shell, with NO adminNav entry. Its only
      // entrance is „Další" at the foot of the OrgSwitcher, which is
      // what sends the user here once the menu starts hiding things.
      { path: "organizations", element: <Lazy><AdminMyOrganizations /></Lazy> },
      { path: "orders", element: <Lazy><AdminOrders /></Lazy> },
      { path: "orders/:orderNumber", element: <Lazy><AdminOrderDetail /></Lazy> },
      { path: "invoices", element: <RequirePageVisible pageId="invoices"><Lazy><AdminInvoices /></Lazy></RequirePageVisible> },
      { path: "invoices/new", element: <RequirePageVisible pageId="invoices"><Lazy><AdminInvoiceNew /></Lazy></RequirePageVisible> },
      { path: "invoices/:invoiceId", element: <RequirePageVisible pageId="invoices"><Lazy><AdminInvoiceDetail /></Lazy></RequirePageVisible> },
      { path: "invoices/:invoiceId/edit", element: <RequirePageVisible pageId="invoices"><Lazy><AdminInvoiceNew /></Lazy></RequirePageVisible> },
      // KAN-26 — oba boardy splynuly do Tabule; staré adresy přesměrují.
      { path: "boards", element: <Lazy><AdminBoards /></Lazy> },
      { path: "operations", element: <LegacyBoardsRedirect groupBy="operation" /> },
      { path: "machines", element: <Lazy><AdminMachines /></Lazy> },
      { path: "stock/movements", element: <Lazy><AdminStockMovements /></Lazy> },
      { path: "storage/locations", element: <Lazy><AdminStorageLocations /></Lazy> },
      { path: "storage/pick-list", element: <Lazy><AdminPickList /></Lazy> },
      // KAN-473 — nabídka hotových dávek bez odběratele → naskladnění.
      { path: "storage/finished-stock", element: <Lazy><AdminFinishedStock /></Lazy> },
      { path: "storage/map", element: <LegacyMapRedirect lens="hall" /> },
      // Dílna D2a — zařízení u míst + tisková fronta (spec §4.5).
      { path: "shopfloor", element: <Lazy><AdminShopfloor /></Lazy> },
      // M4 F3 — "Položky" entity: list → item detail → variant lifecycle
      { path: "materials", element: <Lazy><AdminItems /></Lazy> },
      // KAN-1071 — ceník OPERACE (?process=<slug>). Bez nav položky:
      // M4 rozhodnutí, že ceník bydlí u položek, platí dál — tohle je
      // pohled operace, na který vede odkaz z karty ceny a z uzlu mapy.
      { path: "pricing/rates", element: <Lazy><AdminOperationRates /></Lazy> },
      // KAN-1072 — cenová laboratoř. Nemá vlastní úložiště: přepsané číslo
      // žije jen v jednom requestu a ukládá se tam, kde záznam bydlí.
      { path: "pricing/lab", element: <Lazy><AdminPricingLab /></Lazy> },
      // KAN-1352 — vstupy ceny. Zapisuje každou hodnotu tam, kde bydlí.
      { path: "pricing/inputs", element: <Lazy><AdminPricingInputs /></Lazy> },
      // KAN-1073 — kalkulační schéma tenanta. Řádky, jejich základ a pořadí
      // jsou data autorovaná tady; devět pevných řádků v kódu by znamenalo,
      // že platforma ví, jak se kalkuluje (GENERIC_PLATFORM pravidlo 1).
      { path: "pricing/costing-scheme", element: <Lazy><AdminCostingScheme /></Lazy> },
      { path: "materials/:materialId", element: <Lazy><AdminItemDetail /></Lazy> },
      // M4 F2 — varianta materiálu lifecycle detail (?key=<variant_signature_key>)
      { path: "materials/:materialId/variant", element: <Lazy><AdminMaterialVariantDetail /></Lazy> },
      { path: "suppliers", element: <Lazy><AdminSuppliers /></Lazy> },
      { path: "suppliers/:supplierId", element: <Lazy><AdminSupplierDetail /></Lazy> },
      { path: "purchase-orders", element: <Lazy><AdminPurchaseOrders /></Lazy> },
      { path: "purchase-orders/:poId", element: <Lazy><AdminPurchaseOrderDetail /></Lazy> },
      { path: "material-batches", element: <Lazy><AdminMaterialBatches /></Lazy> },
      // Traceability F5 — deep-link target of printed labels
      // (``/t/{uid}`` redirects here); no nav entry, reached by scan
      // or from cross-links on order/batch pages.
      { path: "trace/:uid", element: <Lazy><AdminTrace /></Lazy> },
      // „Materiály" (mt01/mt02) — tenant vrstva materiálové taxonomie:
      // override matice + vlastní kategorie/vlastnosti na sdílené desce.
      // Stará URL /admin/material-properties redirectuje (nav id —
      // a s ním page_id pravidla viditelnosti — zůstává
      // ``material-properties``, viz adminNav.ts).
      {
        path: "material-properties",
        element: (
          <Navigate to="../material-categories" relative="path" replace />
        ),
      },
      {
        path: "material-categories",
        element: <Lazy><AdminTenantMaterialProperties /></Lazy>,
      },
      // KAN-385: jedna plocha „Aplikace" pro appky i konektory.
      // Záměrně BEZ RequirePageVisible — appky page rule nemají a
      // gatovat celou plochu pravidlem ``integrations`` by plánu bez
      // konektorů sebralo i Aplikace. Konektorová sekce se skrývá sama
      // (``useConnectorsSectionAvailable``).
      { path: "extensions", element: <Lazy><AdminExtensions /></Lazy> },
      // Stará cesta appek. Redirect, ne alias: uložená záložka i odkaz
      // z e-mailu musí skončit na existující ploše, ne na 404.
      { path: "apps", element: <LegacyExtensionsRedirect /> },
      // Jedna routa pro celou appku (KAN-1610): ``<stránka>/<podcesta>``
      // rozebírá host. Dvě routy by při první navigaci appky do podcesty
      // přemountovaly host — a s ním iframe.
      { path: "apps/:appSlug/*", element: <Lazy><AdminAppHost /></Lazy> },
      { path: "partners", element: <RequirePageVisible pageId="partners"><Lazy><AdminPartners /></Lazy></RequirePageVisible> },
      { path: "partners/:code/queue", element: <RequirePageVisible pageId="partners"><Lazy><AdminPartnerQueue /></Lazy></RequirePageVisible> },
      { path: "partners/:code", element: <RequirePageVisible pageId="partners"><Lazy><AdminPartnerDetail /></Lazy></RequirePageVisible> },
      { path: "production", element: <LegacyBoardsRedirect groupBy="order" /> },
      { path: "quotes", element: <Lazy><AdminQuotes /></Lazy> },
      { path: "quotes/new", element: <Lazy><AdminQuoteNew /></Lazy> },
      { path: "quotes/:publicId", element: <Lazy><AdminQuoteDetail /></Lazy> },
      // PR9 Kontakty SSoT: ``/admin/customers/:userId`` je detail
      // kontaktu; list žije na centrálních „Kontaktech".
      { path: "customers/:userId", element: <RequirePageVisible pageId="customers"><Lazy><AdminCustomerDetail /></Lazy></RequirePageVisible> },
      // „Termíny dodání" jako stránka zanikly — rozdělily se podle svého
      // datového švu: kanonické úrovně rychlosti do wizardu „Doprava
      // a termíny" (Krok 04), per-operační varianty do tabu „Termíny"
      // v detailu uzlu mapy výroby.
      // Production settings is split into dedicated nav pages, all backed by
      // the same workspace component and the same page visibility rule.
      // Mapa (KAN-28) — jedna plocha, čočky Proces | Hala. Sama si
      // hlídá viditelnost OBOU původních stránek: právo na jednu
      // čočku ukáže Mapu s tou čočkou, ne 403 na celek.
      { path: "map", element: <Lazy><AdminMap /></Lazy> },
      { path: "production-settings", element: <LegacyMapRedirect lens="process" /> },
      // Vlastní operace (to01/to02) — tenant authoring of owned process
      // definitions; entitlement-gated via page rule ``custom-operations``.
      { path: "custom-operations", element: <RequirePageVisible pageId="custom-operations"><Lazy><AdminTenantOperations /></Lazy></RequirePageVisible> },
      { path: "shipping", element: <Lazy><AdminShipping /></Lazy> },
      // „Trhy" (KAN-1452) — pohled nad zeměmi dodání, DPH a dopravci; bez
      // pravidla viditelnosti, stejně jako obrazovky, jejichž data čte.
      { path: "markets", element: <Lazy><AdminMarkets /></Lazy> },
      { path: "claims", element: <Lazy><AdminClaims /></Lazy> },
      // „Slevy" + „Limity objednávek" splynuly do „Pravidel objednávek".
      // Stará ``/admin/rules`` zůstává jako redirect pro bookmarky.
      {
        path: "rules",
        element: <Navigate to="../order-rules" relative="path" replace />,
      },
      { path: "order-rules", element: <Lazy><AdminOrderRules /></Lazy> },
      { path: "settings/business", element: <Lazy><AdminBusinessInfo /></Lazy> },
      { path: "settings/company", element: <Lazy><AdminCompanyProfile /></Lazy> },
      // Platformní „Profil firmy" (provozovatel + branding platformy).
      // Stejný dual-mount příběh jako ``settings/platform-quote`` níž:
      // položka je ``platformOnly``, takže ji platform admin vidí i na
      // tenant subdoméně — bez routy tady by odkaz 404oval.
      { path: "settings/platform-company", element: <Lazy><AdminPlatformCompanyProfile /></Lazy> },
      { path: "settings/quote", element: <Lazy><AdminTenantQuoteSettings /></Lazy> },
      { path: "settings/platform-quote", element: <Lazy><AdminPlatformQuoteSettings /></Lazy> },
      // Kapacitní předplatba — TÁŽ stránka jako v platform shellu, jen
      // si scope vybere sama podle ``resolveShell()``: tady edituje
      // sazbu poskytovatele, tam výchozí ceník platformy (KAN-1362).
      // KAN-2118 — kapacitní předplatba jen s tarifem ``capacity_contracts``.
      { path: "settings/capacity-pricing", element: <RequirePageVisible pageId="capacity-pricing"><Lazy><AdminCapacityPricing /></Lazy></RequirePageVisible> },
      // „Vaše firma v patičce" + „Branding organizace" splynuly do
      // „Profilu firmy" jako jeho záložky ``?tab=details`` /
      // ``?tab=appearance``.
      // „Matice dovedností" jako samostatná stránka zrušena — per-node
      // detail (matice / modul / sazby / public web) je drawer v
      // inspektoru mapy výroby. Redirect kvůli bookmarkům.
      { path: "carbon", element: <RequirePageVisible pageId="carbon"><Lazy><AdminCarbon /></Lazy></RequirePageVisible> },
      // „Plán" splynul s Fakturací do „Plán a platby" — stará adresa
      // (paywall, upgrade odkazy, bookmarky) skočí na sekci plánu.
      { path: "plan", element: <LegacyPlanRedirect /> },
      // KAN-1364: Kvalitu dodává aplikace — staré adresy přesměrovávají
      // na její host (bookmarky, odkazy v e-mailech), nevrací 404.
      { path: "quality", element: <LegacyQualityRedirect /> },
      { path: "quality/*", element: <LegacyQualityRedirect /> },
      { path: "qa", element: <LegacyQualityRedirect /> },
      { path: "ncr", element: <LegacyQualityRedirect /> },
      // Žebříček dodavatelů je platformní položka; platformní admin
      // v tenant shellu ho má na téže adrese (vzor ``settings/platform-*``).
      { path: "provider-ranking", element: <Lazy><AdminProviderRanking /></Lazy> },
      { path: "orders/:orderNumber/pipeline", element: <Lazy><AdminPipelineEditor /></Lazy> },
      // KAN-304 — editor stromu sestavy. Gate dědí po ploše zakázek
      // (sem se chodí odkazem z detailu), stejně jako sousední
      // ``/pipeline``; vlastní ``RequirePageVisible`` by zavedl
      // page_id, který v katalogu nikdo nespravuje.
      { path: "orders/:orderNumber/assembly", element: <Lazy><AdminOrderAssembly /></Lazy> },
      { path: "groups", element: <Lazy><AdminGroups /></Lazy> },
      // „Pravidla cenotvorby" splynula do „Pravidel objednávek" jako
      // třetí sekce (entitlement gate ``b2b.rules`` si hlídá sekce sama).
      { path: "webhooks", element: <RequirePageVisible pageId="webhooks"><Lazy><AdminWebhooks /></Lazy></RequirePageVisible> },
      { path: "data-residency", element: <RequirePageVisible pageId="data-residency"><Lazy><AdminDataResidency /></Lazy></RequirePageVisible> },
      { path: "data-export", element: <RequirePageVisible pageId="data-export"><Lazy><AdminDataExport /></Lazy></RequirePageVisible> },
      // Bez ``RequirePageVisible`` záměrně: právní dokumenty nejsou
      // prémiová funkce, ale povinnost prodávajícího — žádný
      // entitlement ani page rule je nesmí schovat.
      { path: "legal-documents", element: <Lazy><AdminLegalDocuments /></Lazy> },
      { path: "api-tokens", element: <RequirePageVisible pageId="api-tokens"><Lazy><AdminApiTokens /></Lazy></RequirePageVisible> },
      { path: "sandbox", element: <RequirePageVisible pageId="sandbox"><Lazy><AdminSandbox /></Lazy></RequirePageVisible> },
      { path: "contact-inbox", element: <Lazy><AdminContactInbox /></Lazy> },
      // Pošta (KAN-21). Položka „mail" v ``ADMIN_NAV_GROUPS`` nikdy
      // neměla ``platformOnly``, takže se tenantovi v menu zobrazovala
      // už dřív — jen route existovala pouze v platformním shellu
      // a kliknutí skončilo na ErrorPage. Scope řeší backend
      // (``MailScope``): tenantovi vrátí jeho schránky, ne platformní.
      //
      // ⚠️ ``RequirePageVisible`` tady do KAN-779 NEBYLO, s odůvodněním,
      // že entitlement je o zřizování a ne o čtení už existující pošty.
      // Jenže tenant bez ověřené domény si schránku zřídit ani nemohl,
      // takže „čtení už existující pošty" byla u něj vždycky prázdná
      // obrazovka. Vlastník 2026-09-03: bez připojené domény Poštu
      // vůbec nenabízet. Skrývá ji tedy entitlement ``custom_domain``
      // (pravidlo v seedu) A běhový předpoklad ověřené domény
      // (``PAGES_HIDDEN_UNTIL_READY``) — druhý jmenovaný proto, že mít
      // plán a mít doménu připojenou jsou dvě různé věci.
      {
        path: "mail",
        element: (
          <RequirePageVisible pageId="mail">
            <Lazy><AdminMail /></Lazy>
          </RequirePageVisible>
        ),
      },
      { path: "contacts", element: <Lazy><AdminContacts /></Lazy> },
      // Detail kontaktu je modal nad seznamem — týž element, jiná adresa.
      { path: "contacts/:contactId", element: <Lazy><AdminContacts /></Lazy> },
      // Read-only fronta schvalování pro provider admina. Rozhodovací
      // stránka pro CustomerOrg adminy zůstává na top-level
      // ``/{lang}/approvals`` (deep-linky z e-mailů, bez admin shellu).
      { path: "approvals", element: <RequirePageVisible pageId="approvals"><Lazy><AdminApprovals /></Lazy></RequirePageVisible> },
      // PR9: list B2B kontaktů žije na centrálních „Kontaktech";
      // tady zůstává jen CRM dashboard ProviderCustomerDetail.
      { path: "customer-orgs/:customerOrgId", element: <RequirePageVisible pageId="customer-orgs"><Lazy><AdminProviderCustomerDetail /></Lazy></RequirePageVisible> },
      { path: "stripe-connect", element: <Lazy><AdminStripeConnect /></Lazy> },
      // Stará cesta konektorů — sloučena do „Aplikace" (KAN-385).
      { path: "integrations", element: <LegacyExtensionsRedirect /> },
      { path: "feature-settings", element: <Lazy><AdminFeatureSettings /></Lazy> },
      { path: "menu-layout", element: <Lazy><AdminMenuLayout /></Lazy> },
      // „Výrobní řetězení" jako samostatná stránka zanikla — splynula do
      // mapy výroby, jejíž výchozí sekce ``flow`` JE ten editor.
      { path: "saml", element: <RequirePageVisible pageId="saml"><Lazy><AdminSamlSetup /></Lazy></RequirePageVisible> },
      { path: "oidc", element: <RequirePageVisible pageId="oidc"><Lazy><AdminOidcSetup /></Lazy></RequirePageVisible> },
      { path: "billing", element: <Lazy><AdminBilling /></Lazy> },
      { path: "disputes", element: <Lazy><AdminDisputes /></Lazy> },
      // „Vlastní doména" and „Kde prodáváte" merged into one page.
      // Everything that linked to the old path (readiness list,
      // onboarding wizard) now points at ``embed`` directly; the old
      // ``domain`` route remains only as a redirect for saved bookmarks.
      // No entitlement gate on the PAGE: the embed is available on every
      // plan, Free included — it is how a shop starts, not a paid extra.
      // The custom-domain SECTION inside consults the ``domain`` rule
      // itself, so the Pro gate still bites without taking the whole
      // page away from Free.
      {
        path: "domain",
        element: <Navigate to="../embed" relative="path" replace />,
      },
      { path: "embed", element: <RequirePageVisible pageId="embed"><Lazy><AdminSalesChannels /></Lazy></RequirePageVisible> },
      { path: "audit", element: <RequirePageVisible pageId="audit"><Lazy><AdminAuditLog /></Lazy></RequirePageVisible> },
      {
        // Šablony e-mailů + PDF dokumentů na jedné stránce; rozdělení
        // je filtr v levém sloupci. Každá polovina si drží vlastní
        // page-visibility pravidlo, takže route sama gated NENÍ —
        // stránka schová sekci, na kterou tenant nemá, a když nemá na
        // ani jednu, ukáže stejnou hlášku jako ``RequirePageVisible``.
        path: "settings/templates",
        element: <Lazy><AdminTemplates /></Lazy>,
      },
      {
        // Newsletter — jedna komponenta, scope podle shellu.
        path: "newsletter",
        element: <Lazy><AdminNewsletter /></Lazy>,
      },
      {
        path: "settings/vat",
        element: <Lazy><AdminVatSettings /></Lazy>,
      },
      {
        // Účetní (funkční) měna tenanta. Vlastní stránka, ne sekce v „DPH
        // a daně": tohle se nastavuje JEDNOU, hned po registraci, a od
        // první objednávky je zamčené — jiná životnost než sazby vedle.
        path: "settings/base-currency",
        element: <Lazy><AdminBaseCurrency /></Lazy>,
      },
      {
        // Sjednocené „Číselné řady" — sériová čísla zakázek
        // (Horizon 1 #4) + formát čísel faktur (PR 3 unified-UID).
        path: "settings/number-series",
        element: <Lazy><AdminNumberSeries /></Lazy>,
      },
      // 404 v admin zóně MUSÍ sedět pod AdminLayoutem: top-level
      // catch-all níže by sice cestu chytil, ale ShellFrame na
      // ``/admin/*`` skrývá Header+Footer, takže by se NotFound
      // vykreslil úplně bez chrome. Tady zůstává admin menu stát.
      // KAN-1372 — neznámá cesta, jejíž první segment je slug
      // nainstalované aplikace (staré záložky funkce, která se do
      // aplikace přestěhovala), vede na hostitele aplikace.
      {
        path: "*",
        element: (
          <AdminFallbackRoute>
            <NotFound />
          </AdminFallbackRoute>
        ),
      },
    ]),
  },
  { path: "*", element: <NotFound /> },
];
